import React, { useCallback, useMemo, useState } from 'react';
import filter from 'lodash/filter';
import size from 'lodash/size';
import some from 'lodash/some';
import trim from 'lodash/trim';
import take from 'lodash/take';

import { IsDisabled } from '../../../../../types';
import { CategoryID } from '../../../../categories/categoriesTypes';
import {
  ThreeDStockMenuCategoriesNavCategories,
  ThreeDStockMenuCategoriesNavCategory
} from './ThreeDStockMenuCategoriesNav.types';

import { ThreeDStockMenuCategoriesItem } from './components/ThreeDStockMenuCategoriesItem';
import { ThreeDStockMenuCategoriesItemAmountButton } from './components/ThreeDStockMenuCategoriesItemAmountButton';
import { ButtonHelper } from '../../../../../helpers/buttons/ButtonHelper';
import { NoResults } from '../../../../../helpers/NoResults';

const defaultListCategoriesSize = 5;

interface ThreeDStockMenuCategoriesNavProps {
  currentCategory?: ThreeDStockMenuCategoriesNavCategory;
  categories: ThreeDStockMenuCategoriesNavCategories;
  disabled?: IsDisabled;
  selectCategory: (id: CategoryID, hasChildrenCategories?: boolean) => void;
  alwaysShowAll?: boolean;
}

function ThreeDStockMenuCategoriesNav({
  currentCategory,
  categories,
  disabled,
  selectCategory,
  alwaysShowAll
}: ThreeDStockMenuCategoriesNavProps) {
  const [showAll, setShowAll] = useState(!!alwaysShowAll);

  const listCategories = useMemo(() => {
    return filter(categories, (category) =>
      currentCategory?.id
        ? category.parent?.id === currentCategory?.id
        : !category.parent
    )?.sort((a, b) => {
      return (
        trim(a.localizedName)?.localeCompare(trim(b.localizedName), undefined, {
          numeric: true,
          sensitivity: 'base'
        }) || 0
      );
    });
  }, [currentCategory?.id, categories]);

  const handleClickAll = useCallback(() => {
    selectCategory(currentCategory?.id);
  }, [currentCategory?.id, selectCategory]);

  if (size(listCategories) === 0) {
    return <NoResults className="text-center text-md my-5" />;
  }

  if (
    size(listCategories) <= defaultListCategoriesSize ||
    !!currentCategory?.id
  ) {
    return (
      <ul>
        {currentCategory && (
          <ButtonHelper
            className="flex justify-between w-full items-center px-4 py-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-150 ease-out"
            dataGa={`all-${currentCategory.id}-category`}
            disabled={disabled}
            onClick={handleClickAll}
          >
            <span className="text-md leading-6">
              {`All ${currentCategory?.localizedName} assets`}
            </span>
          </ButtonHelper>
        )}

        {listCategories?.map((category) => (
          <ThreeDStockMenuCategoriesItem
            key={category.id}
            category={category}
            hasChildrenCategories={some(
              categories,
              (cat) => cat.parent?.id === category.id
            )}
            disabled={disabled}
            selectCategory={selectCategory}
          />
        ))}
      </ul>
    );
  }

  const showedCategories = showAll
    ? listCategories
    : take(listCategories, defaultListCategoriesSize);

  return (
    <ul>
      {showedCategories?.map((category) => (
        <ThreeDStockMenuCategoriesItem
          key={category.id}
          category={category}
          hasChildrenCategories={some(
            categories,
            (cat) => cat.parent?.id === category.id
          )}
          disabled={disabled}
          selectCategory={selectCategory}
        />
      ))}
      {!alwaysShowAll && (
        <li>
          <ThreeDStockMenuCategoriesItemAmountButton
            showAll={showAll}
            disabled={disabled}
            setShowAll={setShowAll}
          />
        </li>
      )}
    </ul>
  );
}

export default ThreeDStockMenuCategoriesNav;
